import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, FileText, Copy } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Badge } from "components/ui/badge";
import CallScriptManager from "./CallScriptManager";
import CreateFromTemplateDialog from "./CreateFromTemplateDialog";
import { getCallScripts, deleteCallScript, type CallScriptResponse } from "utils/callScriptsApi";

interface CallScriptsListProps {
  teamId: string;
  specialty?: string | null;
  onCallScriptUpdated?: () => void;
}

const CallScriptsList: React.FC<CallScriptsListProps> = ({
  teamId,
  specialty,
  onCallScriptUpdated,
}) => {
  const [loading, setLoading] = useState(true);
  const [callScripts, setCallScripts] = useState<CallScriptResponse[]>([]);
  const [expandedCallScriptId, setExpandedCallScriptId] = useState<string | null>(null);
  const [showNewCallScriptDialog, setShowNewCallScriptDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [deletingCallScriptId, setDeletingCallScriptId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [callScriptToDelete, setCallScriptToDelete] = useState<CallScriptResponse | null>(null);

  // Fetch all call scripts
  const fetchCallScripts = useCallback(async () => {
    setLoading(true);
    try {
      const scripts = await getCallScripts(teamId);
      const visibleScripts = scripts.filter((s) => {
        const lower = (s.name ?? "").toLowerCase();
        return !lower.includes("auto-generated");
      });
      setCallScripts(visibleScripts);
    } catch (err: any) {
      console.error("Error fetching call scripts:", err);
      toast.error("Failed to load call scripts", {
        description: err.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchCallScripts();
  }, [fetchCallScripts]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!callScriptToDelete?.call_script_id) return;

    setDeletingCallScriptId(callScriptToDelete.call_script_id);
    try {
      await deleteCallScript(callScriptToDelete.call_script_id);
      toast.success("Call script deleted successfully!");

      // Close expanded if deleting expanded script
      if (expandedCallScriptId === callScriptToDelete.call_script_id) {
        setExpandedCallScriptId(null);
      }

      // Refresh list
      await fetchCallScripts();

      if (onCallScriptUpdated) {
        onCallScriptUpdated();
      }
    } catch (err: any) {
      console.error("Error deleting call script:", err);
      toast.error("Failed to delete call script", {
        description: err.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setDeletingCallScriptId(null);
      setShowDeleteDialog(false);
      setCallScriptToDelete(null);
    }
  }, [callScriptToDelete, expandedCallScriptId, fetchCallScripts, onCallScriptUpdated]);

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy");
    }
  };

  // Handle save callback
  const handleCallScriptSaved = useCallback(() => {
    fetchCallScripts();
    if (onCallScriptUpdated) {
      onCallScriptUpdated();
    }
  }, [fetchCallScripts, onCallScriptUpdated]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading call scripts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Call Scripts</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage the questions our AI asks during calls
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowNewCallScriptDialog(false); // Close blank editor if open
              setShowTemplateDialog(true);
            }}
            className="border-[#005151] text-[#005151] hover:bg-[#005151]/5"
          >
            <Copy className="h-4 w-4 mr-2" />
            Create from Template
          </Button>
          <Button
            type="button"
            onClick={() => {
              setExpandedCallScriptId(null); // Collapse any expanded
              setShowNewCallScriptDialog(true);
            }}
            className="bg-[#005151] hover:bg-[#004040] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Call Script
          </Button>
        </div>
      </div>

      {/* Create from Template Dialog */}
      <CreateFromTemplateDialog
        open={showTemplateDialog}
        teamId={teamId}
        onOpenChange={setShowTemplateDialog}
        onSuccess={(callScriptId) => {
          setShowTemplateDialog(false);
          fetchCallScripts().then(() => {
            // Expand the newly created call script
            setExpandedCallScriptId(callScriptId);
          });
          if (onCallScriptUpdated) {
            onCallScriptUpdated();
          }
        }}
      />

      {/* New Call Script Dialog */}
      {showNewCallScriptDialog && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600 text-sm">
              Select sample questions and create custom questions for your call scripts.
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowNewCallScriptDialog(false);
                setExpandedCallScriptId(null);
              }}
            >
              Cancel
            </Button>
          </div>
          <CallScriptManager
            mode="account"
            teamId={teamId}
            specialty={specialty}
            onSave={() => {
              setShowNewCallScriptDialog(false);
              handleCallScriptSaved();
            }}
          />
        </div>
      )}

      {/* Empty State */}
      {callScripts.length === 0 && !showNewCallScriptDialog && (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              No call scripts yet
            </h4>
            <p className="text-gray-600 text-center mb-4">
              Create your first call script to get started. Call scripts define the questions
              our AI will ask during insurance calls.
            </p>
            <Button
              type="button"
              onClick={() => setShowNewCallScriptDialog(true)}
              className="bg-[#005151] hover:bg-[#004040] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Call Script
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Call Scripts List */}
      {callScripts.length > 0 && (
        <Accordion
          type="single"
          collapsible
          value={expandedCallScriptId || undefined}
          onValueChange={(value) => setExpandedCallScriptId(value || null)}
          className="space-y-2"
        >
          {callScripts.map((callScript) => (
            <AccordionItem
              key={callScript.call_script_id || ""}
              value={callScript.call_script_id || ""}
              className="border border-gray-200 rounded-lg"
            >
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center space-x-4 flex-1 text-left">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-semibold text-gray-900">
                          {callScript.name || "Untitled Call Script"}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {callScript.questions.length} question{callScript.questions.length !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Last updated: {formatDate(callScript.last_updated_ts)}
                      </p>
                      {callScript.call_script_id && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500 font-mono">
                            ID: {callScript.call_script_id}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(callScript.call_script_id!);
                            }}
                            className="h-6 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            aria-label="Copy call script id"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCallScriptToDelete(callScript);
                      setShowDeleteDialog(true);
                    }}
                    disabled={deletingCallScriptId === callScript.call_script_id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                {callScript.call_script_id && (
                  <CallScriptManager
                    mode="account"
                    teamId={teamId}
                    specialty={specialty}
                    callScriptId={callScript.call_script_id}
                    onSave={handleCallScriptSaved}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Call Script</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{callScriptToDelete?.name || "this call script"}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setCallScriptToDelete(null);
              }}
              disabled={!!deletingCallScriptId}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={!!deletingCallScriptId}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingCallScriptId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallScriptsList;
